import { create } from "zustand";

export interface DialogOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "danger" | "warning" | "info" | "success";
  isAlert?: boolean;
}

interface DialogState {
  isOpen: boolean;
  options: DialogOptions;
  resolvePromise: ((value: boolean) => void) | null;
  openConfirm: (options: DialogOptions) => Promise<boolean>;
  openAlert: (options: Omit<DialogOptions, "cancelText" | "isAlert">) => Promise<void>;
  close: (result: boolean) => void;
}

export const useDialogStore = create<DialogState>((set, get) => ({
  isOpen: false,
  options: {
    title: "",
    message: "",
    confirmText: "Aceptar",
    cancelText: "Cancelar",
    variant: "info",
    isAlert: false,
  },
  resolvePromise: null,

  openConfirm: (options: DialogOptions) => {
    return new Promise<boolean>((resolve) => {
      set({
        isOpen: true,
        options: {
          title: options.title || "¿Estás seguro?",
          message: options.message,
          confirmText: options.confirmText || "Confirmar",
          cancelText: options.cancelText || "Cancelar",
          variant: options.variant || "warning",
          isAlert: false,
        },
        resolvePromise: resolve,
      });
    });
  },

  openAlert: (options) => {
    return new Promise<void>((resolve) => {
      set({
        isOpen: true,
        options: {
          title: options.title || "Atención",
          message: options.message,
          confirmText: options.confirmText || "Entendido",
          variant: options.variant || "info",
          isAlert: true,
        },
        resolvePromise: () => resolve(),
      });
    });
  },

  close: (result: boolean) => {
    const { resolvePromise } = get();
    if (resolvePromise) {
      resolvePromise(result);
    }
    set({
      isOpen: false,
      resolvePromise: null,
    });
  },
}));

/**
 * Convenient async helper functions usable anywhere without hooks
 */
export function confirmDialog(options: DialogOptions | string): Promise<boolean> {
  const opts = typeof options === "string" ? { message: options } : options;
  return useDialogStore.getState().openConfirm(opts);
}

export function alertDialog(
  messageOrOptions: string | Omit<DialogOptions, "cancelText" | "isAlert">,
  title?: string
): Promise<void> {
  const opts =
    typeof messageOrOptions === "string"
      ? { message: messageOrOptions, title: title || "Atención" }
      : messageOrOptions;
  return useDialogStore.getState().openAlert(opts);
}
